import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Mail, Phone, Linkedin, MapPin, Building2, Edit, Trash2, Tag, CheckCircle2, Plus, User, MessageSquare, PhoneCall, Send, History, FolderOpen, Upload, X, FileText, Image, File, Ban, MailCheck, XCircle, Search, RotateCcw, RefreshCw } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import AttachFromLibraryModal from '../../components/AttachFromLibraryModal';
import DraggableDialog from '../../components/DraggableDialog';
import VariablesHelper, { replaceVariables } from '../../components/VariablesHelper';
import { TemplateCombobox } from '../../components/TemplateCombobox';
import { LogCallDialog } from '../../components/LogCallDialog';
import EmailMergeDialog from '../../components/EmailMergeDialog';
import ActiveCallPopup from '../../components/ActiveCallPopup';
import TelnyxWebRTCCall from '../../components/TelnyxWebRTCCall';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Pipeline statuses for Franchise leads
const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'negotiating', label: 'Negotiating', color: 'bg-orange-100 text-orange-700' },
  { value: 'won', label: 'Won', color: 'bg-green-100 text-green-700' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' },
];

// Lead statuses 
const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-slate-100 text-slate-700' },
  { value: 'interested', label: 'Interested', color: 'bg-green-100 text-green-700' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-red-100 text-red-700' },
];

// Franchise interest levels
const FRANCHISE_INTERESTS = [
  { value: 'high', label: 'High Interest', color: 'bg-green-100 text-green-700' },
  { value: 'medium', label: 'Medium Interest', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'low', label: 'Low Interest', color: 'bg-slate-100 text-slate-700' },
];

const getStatusBadge = (status) => {
  const s = STATUSES.find(x => x.value === status) || STATUSES[0];
  return <Badge className={s.color}>{s.label}</Badge>;
};

const getLeadStatusBadge = (leadStatus) => {
  const s = LEAD_STATUSES.find(x => x.value === leadStatus);
  if (!s) return null;
  return <Badge className={s.color}>{s.label}</Badge>;
};

const getInterestBadge = (interest) => {
  const i = FRANCHISE_INTERESTS.find(x => x.value === interest);
  if (!i) return null;
  return <Badge variant="outline" className={i.color}>{i.label}</Badge>;
};

// Interest colors for tags
const INTEREST_COLORS = {
  'Franchise': 'bg-indigo-100 text-indigo-700',
  'JobTalk': 'bg-blue-100 text-blue-700',
  'NotInterested': 'bg-red-100 text-red-700',
  'Leadership': 'bg-purple-100 text-purple-700',
};

// Interest Tags Component
const InterestInTags = ({ tags }) => {
  if (!tags || tags.length === 0) return null;
  const tagColors = INTEREST_COLORS;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map(tag => (
        <Badge key={tag} variant="outline" className={tagColors[tag] || 'bg-slate-100 text-slate-700'}>
          {tag}
        </Badge>
      ))}
    </div>
  );
};

export default function FranchiseLeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Core data
  const [lead, setLead] = useState(null);
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Cache key for lists
  const LISTS_CACHE_KEY = 'franchise_lists_cache';

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [attachLibraryOpen, setAttachLibraryOpen] = useState(false);
  const [addToListDialogOpen, setAddToListDialogOpen] = useState(false);
  const [selectedListToAdd, setSelectedListToAdd] = useState('');

  // Form states
  const [formData, setFormData] = useState({});
  const [taskForm, setTaskForm] = useState({ title: '', description: '', due_date: '', due_time: '09:00', task_type: 'Call' });
  const [newNote, setNewNote] = useState('');
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', bcc: '', subject: '', body: '', template_id: '', attachments: [] });
  const [smsForm, setSmsForm] = useState({ to: '', message: '', template_id: '' });
  const [callForm, setCallForm] = useState({
    phone_number: '',
    call_outcome: 'connected',
    duration_seconds: 0,
    notes: '',
    schedule_followup: false,
    followup_date: '',
    followup_time: '09:00',
    mark_dnc: false,
    send_email_after: false,
    email_template_id: '',
    recording_id: null
  });

  // UI states
  const [activeTab, setActiveTab] = useState('activity');
  const [activityFilter, setActivityFilter] = useState('all');
  
  // Lead Search states
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [allLeads, setAllLeads] = useState([]);
  
  // Click-to-call state
  const [callingNumber, setCallingNumber] = useState(null);
  const [activeCallPopupOpen, setActiveCallPopupOpen] = useState(false);
  const [activeCallStatus, setActiveCallStatus] = useState('calling'); // 'calling', 'connected', 'ended'
  const [activeCallNumber, setActiveCallNumber] = useState(null);
  
  // Telnyx WebRTC state
  const [telnyxStatus, setTelnyxStatus] = useState({ configured: false, webrtc_enabled: false });
  const [webrtcCallOpen, setWebrtcCallOpen] = useState(false);
  const [webrtcCallNumber, setWebrtcCallNumber] = useState(null);
  
  const fileInputRef = useRef(null);

  // Fetch Telnyx status
  useEffect(() => {
    const fetchTelnyxStatus = async () => {
      try {
        const res = await axios.get(`${API}/telnyx/status`);
        setTelnyxStatus(res.data);
      } catch {
        console.log('Telnyx not configured');
      }
    };
    fetchTelnyxStatus();
  }, []);

  // Fetch all data with optional cache usage for lists
  const fetchData = useCallback(async (forceReloadLists = false) => {
    setLoading(true);
    try {
      // Try to get lists from cache first (unless force reload)
      let listsData = null;
      if (!forceReloadLists) {
        const cachedLists = sessionStorage.getItem(LISTS_CACHE_KEY);
        if (cachedLists) {
          try {
            const parsed = JSON.parse(cachedLists);
            if (Date.now() - parsed.timestamp < 10 * 60 * 1000) { // 10 min cache
              listsData = parsed.lists;
            }
          } catch { /* ignore */ }
        }
      }
      
      // Fetch lead data and other data (lists only if not cached)
      const promises = [
        axios.get(`${API}/franchise/leads/${id}`),
        listsData ? Promise.resolve({ data: { lists: listsData } }) : axios.get(`${API}/franchise/lists`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/franchise/leads/${id}/communications`).catch(() => ({ data: [] })),
        axios.get(`${API}/franchise/leads/${id}/tasks`).catch(() => ({ data: [] })),
      ];
      
      const [leadRes, listsRes, templatesRes, commsRes, tasksRes] = await Promise.all(promises);
      
      setLead(leadRes.data);
      setFormData(leadRes.data);
      
      const fetchedLists = listsRes.data.lists || listsRes.data || [];
      setLists(fetchedLists);
      
      // Cache lists if we fetched fresh data
      if (!listsData || forceReloadLists) {
        sessionStorage.setItem(LISTS_CACHE_KEY, JSON.stringify({
          lists: fetchedLists,
          timestamp: Date.now()
        }));
      }
      
      setTemplates(templatesRes.data || []);
      setCommunications(commsRes.data?.communications || commsRes.data || []);
      setTasks(tasksRes.data?.tasks || tasksRes.data || []);
      
      // Fetch notes
      try {
        const notesRes = await axios.get(`${API}/franchise/leads/${id}/notes`);
        setNotes(notesRes.data?.notes || notesRes.data || []);
      } catch { setNotes([]); }
      
      // Fetch audit logs
      try {
        const logsRes = await axios.get(`${API}/franchise/leads/${id}/audit-logs`);
        setAuditLogs(logsRes.data || []);
      } catch { setAuditLogs([]); }
      
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/admin/franchise/leads');
      } else {
        toast.error('Failed to load lead');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, navigate, LISTS_CACHE_KEY]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch all leads for search
  useEffect(() => {
    const fetchAllLeads = async () => {
      try {
        const res = await axios.get(`${API}/franchise/leads?limit=500`);
        // API returns 'items' not 'leads'
        setAllLeads(res.data?.items || res.data?.leads || []);
      } catch (error) {
        console.error('Failed to fetch leads for search:', error);
      }
    };
    fetchAllLeads();
  }, []);

  // Filtered search leads
  const filteredSearchLeads = useMemo(() => {
    if (!leadSearchQuery.trim()) return [];
    const query = leadSearchQuery.toLowerCase();
    return allLeads
      .filter(l => 
        l.id !== id && (
          (l.first_name && l.first_name.toLowerCase().includes(query)) ||
          (l.last_name && l.last_name.toLowerCase().includes(query)) ||
          (l.email && l.email.toLowerCase().includes(query)) ||
          (l.current_employer && l.current_employer.toLowerCase().includes(query)) ||
          (l.territory_preference && l.territory_preference.toLowerCase().includes(query))
        )
      )
      .slice(0, 10);
  }, [leadSearchQuery, allLeads, id]);

  // Initialize email/SMS forms when lead loads
  useEffect(() => {
    if (lead) {
      setEmailForm(f => ({ ...f, to: lead.email || '' }));
      setSmsForm(f => ({ ...f, to: lead.phone || '' }));
    }
  }, [lead]);

  // Handlers
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/franchise/leads/${id}`, formData);
      toast.success('Lead updated');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/franchise/leads/${id}`);
      toast.success('Lead deleted');
      navigate('/admin/franchise/leads');
    } catch {
      toast.error('Failed to delete lead');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFlag = async (field, value) => {
    try {
      await axios.put(`${API}/franchise/leads/${id}`, { [field]: value });
      setLead(prev => ({ ...prev, [field]: value }));
      toast.success(`${field === 'dnc' ? 'Do Not Call' : field === 'dne' ? 'Do Not Email' : field} updated`);
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleCreateTask = async () => {
    if (!taskForm.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      let dueDateTime = taskForm.due_date;
      if (taskForm.due_date && taskForm.due_time) {
        dueDateTime = `${taskForm.due_date}T${taskForm.due_time}`;
      }
      await axios.post(`${API}/franchise/tasks`, { 
        lead_id: id, 
        title: taskForm.title,
        description: taskForm.description,
        due_date: dueDateTime,
        task_type: taskForm.task_type
      });
      toast.success('Task created');
      setTaskDialogOpen(false);
      setTaskForm({ title: '', description: '', due_date: '', due_time: '09:00', task_type: 'Call' });
      fetchData();
    } catch { toast.error('Failed to create task'); }
    finally { setSaving(false); }
  };

  const handleToggleTask = async (taskId, completed) => {
    try {
      await axios.put(`${API}/franchise/tasks/${taskId}`, { completed: !completed });
      fetchData();
    } catch { toast.error('Failed to update task'); }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/franchise/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchData();
    } catch { toast.error('Failed to delete task'); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/franchise/leads/${id}/notes`, { content: newNote });
      toast.success('Note added');
      setNewNote('');
      fetchData();
    } catch { toast.error('Failed to add note'); }
    finally { setSaving(false); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/franchise/leads/${id}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchData();
    } catch { toast.error('Failed to delete note'); }
  };

  const handleSendEmail = async () => {
    if (!emailForm.to || !emailForm.subject || !emailForm.body) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const finalSubject = replaceVariables(emailForm.subject, lead);
      const finalBody = replaceVariables(emailForm.body, lead);
      
      await axios.post(`${API}/communications/email`, {
        lead_id: id,
        to: emailForm.to,
        cc: emailForm.cc ? emailForm.cc.split(',').map(e => e.trim()).filter(Boolean) : [],
        bcc: emailForm.bcc ? emailForm.bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
        subject: finalSubject,
        body: finalBody,
        attachments: []
      });
      
      toast.success('Email sent');
      setEmailDialogOpen(false);
      setEmailForm({ to: lead?.email || '', cc: '', bcc: '', subject: '', body: '', template_id: '', attachments: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSaving(false);
    }
  };

  const handleSendSms = async () => {
    if (!smsForm.to || !smsForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const finalMessage = replaceVariables(smsForm.message, lead);
      
      // Try to send via Telnyx first (actual SMS delivery)
      try {
        await axios.post(`${API}/telnyx/sms`, {
          to_number: smsForm.to,
          message: finalMessage,
          lead_id: id
        });
        toast.success('SMS sent via Telnyx');
      } catch (telnyxError) {
        // If Telnyx fails, fall back to just logging
        await axios.post(`${API}/communications/sms`, {
          lead_id: id,
          to: smsForm.to,
          message: finalMessage
        });
        toast.success('SMS logged (delivery service unavailable)');
      }
      
      setSmsDialogOpen(false);
      setSmsForm({ to: lead?.phone || '', message: '', template_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCommunication = async (commId) => {
    try {
      await axios.delete(`${API}/franchise/leads/${id}/communications/${commId}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEmailTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEmailForm(f => ({
        ...f,
        template_id: templateId,
        subject: template.subject || f.subject,
        body: template.content || f.body
      }));
    }
  };

  const handleSmsTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSmsForm(f => ({
        ...f,
        template_id: templateId,
        message: template.content || f.message
      }));
    }
  };

  const insertVariable = (variable, formType, field) => {
    if (formType === 'email') {
      setEmailForm(f => ({ ...f, [field]: f[field] + variable }));
    } else if (formType === 'sms') {
      setSmsForm(f => ({ ...f, message: f.message + variable }));
    }
  };

  // Open dialogs with pre-filled data
  const openEmailDialog = () => {
    setEmailForm(f => ({ ...f, to: lead?.email || '' }));
    setEmailDialogOpen(true);
  };

  const openSmsDialog = () => {
    setSmsForm(f => ({ ...f, to: lead?.phone || '' }));
    setSmsDialogOpen(true);
  };

  const openCallDialog = () => {
    setCallForm(f => ({
      ...f,
      phone_number: lead?.phone || '',
      call_outcome: 'connected',
      duration_seconds: 0,
      notes: '',
      schedule_followup: false,
      followup_date: '',
      followup_time: '09:00',
      mark_dnc: false,
      send_email_after: false,
      email_template_id: '',
      recording_id: null
    }));
    setCallDialogOpen(true);
  };

  // Click-to-call function - uses WebRTC when available, falls back to tel: link
  const handleClickToCall = async (phoneNumber) => {
    if (!phoneNumber) return;
    
    // If Telnyx not configured, use native phone dialer
    if (!telnyxStatus.configured) {
      window.location.href = `tel:${phoneNumber}`;
      toast.info('Opening phone dialer...');
      return;
    }
    
    // For now, use server-side calling (more reliable than browser WebRTC)
    // WebRTC requires proper Telnyx SIP credentials which may not be fully configured
    // TODO: Enable WebRTC once Telnyx credentials are verified working
    
    // Server-side call (uses Telnyx to connect two phones - your phone and the lead's phone)
    setCallingNumber(phoneNumber);
    setActiveCallNumber(phoneNumber);
    setActiveCallStatus('calling');
    setActiveCallPopupOpen(true);
    
    try {
      await axios.post(`${API}/telnyx/call`, {
        to_number: phoneNumber,
        lead_id: id
      });
      toast.success(`Calling ${phoneNumber}...`);
      setTimeout(() => {
        setActiveCallStatus('connected');
      }, 3000);
    } catch (error) {
      // Fall back to tel: link for any Telnyx error
      window.location.href = `tel:${phoneNumber}`;
      toast.info('Opening phone dialer...');
      setActiveCallPopupOpen(false);
    } finally {
      setTimeout(() => setCallingNumber(null), 3000);
    }
  };

  // Handle WebRTC call connected - pre-fill call form for logging
  const handleWebRTCCallConnected = (dialedNumber) => {
    setCallForm(f => ({ 
      ...f, 
      phone_number: dialedNumber || webrtcCallNumber || f.phone_number,
      notes: '',
      call_outcome: 'connected',
      duration_seconds: 0
    }));
  };

  // Handle WebRTC call ended - open log call dialog with duration
  const handleWebRTCCallEnded = (duration) => {
    setCallForm(f => ({ ...f, duration_seconds: duration }));
    setWebrtcCallOpen(false);
    setWebrtcCallNumber(null);
    setCallDialogOpen(true);
  };

  // Handle duration update from WebRTC component
  const handleDurationUpdate = (duration) => {
    setCallForm(f => ({ ...f, duration_seconds: duration }));
  };

  // Handle WebRTC error - fall back to native phone dialer
  const handleWebRTCError = (errorMsg) => {
    toast.error(`WebRTC call failed: ${errorMsg}`);
    setWebrtcCallOpen(false);
    setWebrtcCallNumber(null);
    // Fall back to native phone dialer
    if (webrtcCallNumber) {
      window.location.href = `tel:${webrtcCallNumber}`;
      toast.info('Opening phone dialer as fallback...');
    }
  };

  // Disconnect/end the active call
  const handleDisconnectCall = async (elapsedSeconds) => {
    try {
      // Try to hang up via Telnyx (if supported)
      await axios.post(`${API}/telnyx/hangup`, { phone_number: activeCallNumber }).catch(() => {});
    } catch (e) {
      // Ignore errors - just close the popup
    }
    setActiveCallStatus('ended');
    toast.info('Call ended');
    
    // Pre-fill the call form with duration for logging
    setCallForm(f => ({
      ...f,
      phone_number: activeCallNumber,
      duration_seconds: elapsedSeconds || 0
    }));
  };

  // Open log call dialog from the active call popup
  const handleLogCallFromPopup = (elapsedSeconds) => {
    // Pre-fill form with call details
    setCallForm(f => ({
      ...f,
      phone_number: activeCallNumber || f.phone_number,
      duration_seconds: elapsedSeconds || 0,
      call_outcome: 'connected'
    }));
    // Close the popup and open log call dialog
    setActiveCallPopupOpen(false);
    setActiveCallNumber(null);
    setActiveCallStatus('calling');
    setCallDialogOpen(true);
  };

  const handleLogCall = async () => {
    if (!callForm.phone_number) {
      toast.error('Phone number is required');
      return;
    }
    setSaving(true);
    try {
      // Log the call using the shared communications endpoint (supports both CRM and Franchise)
      await axios.post(`${API}/communications/call`, {
        lead_id: id,
        phone_number: callForm.phone_number,
        call_outcome: callForm.call_outcome,
        duration_seconds: callForm.duration_seconds,
        notes: callForm.notes,
        recording_id: callForm.recording_id
      });
      
      // Mark DNC if checked
      if (callForm.mark_dnc) {
        await axios.put(`${API}/franchise/leads/${id}`, { dnc: true });
      }
      
      // Create follow-up task if checked
      if (callForm.schedule_followup && callForm.followup_date) {
        const dueDateTime = `${callForm.followup_date}T${callForm.followup_time || '09:00'}`;
        await axios.post(`${API}/franchise/tasks`, {
          lead_id: id,
          title: `Follow-up call with ${lead?.first_name || ''} ${lead?.last_name || ''}`.trim(),
          task_type: 'Call',
          due_date: dueDateTime
        });
      }
      
      toast.success('Call logged successfully');
      setCallDialogOpen(false);
      // Reset call form
      setCallForm({
        phone_number: lead?.phone || '',
        call_outcome: 'connected',
        duration_seconds: 0,
        notes: '',
        schedule_followup: false,
        followup_date: '',
        followup_time: '09:00',
        mark_dnc: false,
        send_email_after: false,
        email_template_id: '',
        recording_id: null
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log call');
    } finally {
      setSaving(false);
    }
  };

  // List management
  const handleAddToList = async () => {
    if (!selectedListToAdd) {
      toast.error('Please select a list');
      return;
    }
    try {
      await axios.post(`${API}/franchise/leads/${id}/lists`, { list_ids: [selectedListToAdd] });
      toast.success('Added to list');
      setAddToListDialogOpen(false);
      setSelectedListToAdd('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to list');
    }
  };

  const handleRemoveFromList = async (listId) => {
    try {
      await axios.delete(`${API}/franchise/leads/${id}/lists/${listId}`);
      toast.success('Removed from list');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove from list');
    }
  };

  // Reset - clear search and use cached data
  const handleReset = () => {
    setLeadSearchQuery('');
    setLeadSearchOpen(false);
    fetchData(false); // Use cached lists
    toast.info('Reset - using cached data');
  };

  // Reload - clear search and fetch fresh data from database
  const handleReload = () => {
    setLeadSearchQuery('');
    setLeadSearchOpen(false);
    setRefreshing(true);
    fetchData(true); // Force reload lists from DB
    toast.info('Reloading from database...');
  };

  // Get lists that the lead is NOT already in (for the add dropdown)
  const availableLists = lists.filter(l => !(lead?.list_ids || []).includes(l.id));

  // Computed values
  const filteredCommunications = useMemo(() => {
    if (activityFilter === 'all') return communications;
    return communications.filter(c => c.type === activityFilter);
  }, [communications, activityFilter]);

  const pendingTasks = useMemo(() => tasks.filter(t => !t.completed), [tasks]);
  const completedTasks = useMemo(() => tasks.filter(t => t.completed), [tasks]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Lead not found</p>
        <Button onClick={() => navigate('/admin/franchise/leads')} className="mt-4">Back to Leads</Button>
      </div>
    );
  }

  const leadLists = lists.filter(l => (lead.list_ids || []).includes(l.id));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Lead Search Bar */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/franchise/leads')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 max-w-md relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search franchise leads..."
              value={leadSearchQuery}
              onChange={(e) => { setLeadSearchQuery(e.target.value); if (e.target.value.trim()) setLeadSearchOpen(true); else setLeadSearchOpen(false); }}
              onFocus={() => leadSearchQuery.trim() && setLeadSearchOpen(true)}
              onBlur={() => setTimeout(() => setLeadSearchOpen(false), 200)}
              className="pl-9 h-10 bg-white"
              data-testid="lead-search-input"
            />
            {leadSearchQuery && (
              <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onMouseDown={(e) => e.preventDefault()} onClick={() => { setLeadSearchQuery(''); setLeadSearchOpen(false); }}>
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {leadSearchOpen && filteredSearchLeads.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
              {filteredSearchLeads.map((l) => (
                <button key={l.id} className="w-full px-3 py-2 text-left hover:bg-slate-100 flex items-center gap-3 border-b last:border-0" onMouseDown={(e) => e.preventDefault()} onClick={() => { setLeadSearchOpen(false); setLeadSearchQuery(''); navigate(`/admin/franchise/leads/${l.id}`); }}>
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-indigo-600" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{l.first_name} {l.last_name}</p>
                    <p className="text-xs text-slate-500 truncate">{l.current_employer || l.email || 'No company'}</p>
                  </div>
                  {l.dnc && <Badge variant="outline" className="text-red-600 text-xs">DNC</Badge>}
                </button>
              ))}
            </div>
          )}
          {leadSearchOpen && leadSearchQuery.trim() && filteredSearchLeads.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-4 text-center text-slate-500 text-sm">No leads found</div>
          )}
        </div>
        {/* Reset and Reload buttons */}
        <Button
          variant="outline"
          size="icon"
          onClick={handleReset}
          title="Reset (use cached data)"
          className="h-10 w-10"
          data-testid="reset-btn"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleReload}
          disabled={refreshing}
          title="Reload from database"
          className="h-10 w-10"
          data-testid="reload-btn"
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{lead.first_name} {lead.last_name}</h1>
              <button onClick={() => setEditDialogOpen(true)} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-indigo-600" data-testid="edit-lead-btn" title="Edit Lead">
                <Edit className="h-4 w-4" />
              </button>
            </div>
            <p className="text-slate-600 mt-1">{lead.current_employer || 'No employer'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={openEmailDialog} data-testid="email-btn" disabled={lead.dne} title={lead.dne ? "Do Not Email is enabled" : "Send Email"}>
            <Mail className="h-4 w-4 mr-1" /> Email
          </Button>
          <Button variant="outline" size="sm" onClick={openSmsDialog} data-testid="sms-btn">
            <MessageSquare className="h-4 w-4 mr-1" /> SMS
          </Button>
          <Button variant="outline" size="sm" onClick={openCallDialog} data-testid="call-btn" disabled={lead.dnc} title={lead.dnc ? "Do Not Call is enabled" : "Log Call"}>
            <PhoneCall className="h-4 w-4 mr-1" /> Log Call
          </Button>
          <Button variant="outline" size="sm" onClick={() => setTaskDialogOpen(true)} data-testid="task-btn">
            <Plus className="h-4 w-4 mr-1" /> Task
          </Button>
          <Button variant="outline" className="text-red-600" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-btn">
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Sidebar */}
        <div className="space-y-4">
          {/* Profile Card */}
          <Card className="relative">
            <CardContent className="pt-6">
              {/* Edit/Delete buttons in top right */}
              <div className="absolute top-3 right-3 flex items-center gap-1">
                <button onClick={() => setEditDialogOpen(true)} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-indigo-600" title="Edit Lead">
                  <Edit className="h-4 w-4" />
                </button>
                <button onClick={() => setDeleteDialogOpen(true)} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-red-600" title="Delete Lead">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-slate-900">{lead.first_name} {lead.last_name}</h2>
                <p className="text-slate-500 mt-1">{lead.current_employer || 'No employer'}</p>
                {lead.territory_preference && <p className="text-indigo-600 font-medium mt-0.5">{lead.territory_preference}</p>}
                <div className="mt-2 flex items-center justify-center gap-2">
                  {getStatusBadge(lead.status)}
                </div>
                
                {/* Editable Interest In Tags */}
                <div className="mt-3">
                  <p className="text-xs text-slate-500 mb-1.5">Interested In</p>
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {['Franchise', 'Leadership', 'JobTalk'].map(tag => {
                      const isSelected = (lead.interest_in || []).includes(tag);
                      return (
                        <button
                          key={tag}
                          onClick={async () => {
                            try {
                              const currentInterests = lead.interest_in || [];
                              const newInterests = isSelected 
                                ? currentInterests.filter(t => t !== tag)
                                : [...currentInterests, tag];
                              await axios.put(`${API}/franchise/leads/${id}`, { interest_in: newInterests });
                              toast.success(`${isSelected ? 'Removed' : 'Added'} ${tag}`);
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to update interest');
                            }
                          }}
                          className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all ${
                            isSelected 
                              ? INTEREST_COLORS[tag] || 'bg-indigo-100 text-indigo-700'
                              : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                          }`}
                          title={isSelected ? `Remove ${tag}` : `Add ${tag}`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 pt-4 border-t">
                <Button size="icon" onClick={openEmailDialog} data-testid="email-btn-sidebar" className={`h-9 w-9 ${lead.dne ? 'bg-slate-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`} title={lead.dne ? "Do Not Email" : "Send Email"} disabled={lead.dne}>
                  <Mail className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={openSmsDialog} data-testid="sms-btn-sidebar" title="Send SMS" className="h-9 w-9">
                  <MessageSquare className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={openCallDialog} data-testid="call-btn-sidebar" title={lead.dnc ? "Do Not Call" : "Log Call"} className="h-9 w-9" disabled={lead.dnc}>
                  <PhoneCall className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => setTaskDialogOpen(true)} data-testid="task-btn-sidebar" title="Add Task" className="h-9 w-9">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Contact Information</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {lead.email && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Email</p>
                    <div className="flex items-center gap-2">
                      <a href={`mailto:${lead.email}`} className="text-sm text-indigo-600 hover:underline">{lead.email}</a>
                      <button onClick={openEmailDialog} className="p-1 hover:bg-indigo-100 rounded text-indigo-600" title="Send Email">
                        <Send className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {lead.phone && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Phone</p>
                    <div className="flex items-center gap-2">
                      {lead.dnc ? (
                        <span className="text-sm text-slate-400 flex items-center gap-1">
                          {lead.phone} <Ban className="h-3 w-3 text-red-400" title="Do Not Call" />
                        </span>
                      ) : (
                        <>
                          <button 
                            onClick={() => handleClickToCall(lead.phone)} 
                            disabled={callingNumber === lead.phone}
                            className="text-sm text-slate-700 hover:text-green-600 hover:underline flex items-center gap-1"
                            title="Click to call"
                          >
                            {callingNumber === lead.phone ? (
                              <><Loader2 className="h-3 w-3 animate-spin" /> Calling...</>
                            ) : (
                              <>{lead.phone} <PhoneCall className="h-3 w-3" /></>
                            )}
                          </button>
                          <button onClick={openSmsDialog} className="p-1 hover:bg-green-100 rounded text-green-600" title="Send SMS">
                            <MessageSquare className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {lead.linkedin_url && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Linkedin className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">LinkedIn</p>
                    <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">View Profile</a>
                  </div>
                </div>
              )}
              {(lead.city || lead.state) && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Location</p>
                    <p className="text-sm text-slate-700">{[lead.address, lead.city, lead.state, lead.zip_code].filter(Boolean).join(', ')}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lists */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">Lists</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setAddToListDialogOpen(true)} className="h-7 px-2" data-testid="add-to-list-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {leadLists.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-2">Not in any lists</p>
              ) : (
                <div className="space-y-1.5">
                  {leadLists.map(list => (
                    <div key={list.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 group">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-indigo-500" />
                        <span className="text-sm font-medium">{list.name}</span>
                      </div>
                      <button 
                        onClick={() => handleRemoveFromList(list.id)}
                        className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove from list"
                        data-testid={`remove-from-list-${list.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Quick Actions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                <div className="flex items-center gap-2">
                  <Ban className={`h-4 w-4 ${lead.dnc ? 'text-red-500' : 'text-slate-400'}`} />
                  <span className="text-sm">Do Not Call</span>
                </div>
                <Checkbox checked={lead.dnc || false} onCheckedChange={(checked) => handleToggleFlag('dnc', checked)} />
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                <div className="flex items-center gap-2">
                  <MailCheck className={`h-4 w-4 ${lead.dne ? 'text-red-500' : 'text-slate-400'}`} />
                  <span className="text-sm">Do Not Email</span>
                </div>
                <Checkbox checked={lead.dne || false} onCheckedChange={(checked) => handleToggleFlag('dne', checked)} />
              </div>
            </CardContent>
          </Card>

          {/* Lead Details */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Lead Details</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {lead.lead_owner && (
                <div className="flex justify-between"><span className="text-slate-500">Owner</span><span className="font-medium">{lead.lead_owner}</span></div>
              )}
              {lead.lead_source && (
                <div className="flex justify-between"><span className="text-slate-500">Source</span><span className="font-medium">{lead.lead_source}</span></div>
              )}
              {lead.franchise_interest && (
                <div className="flex justify-between"><span className="text-slate-500">Interest Level</span>{getInterestBadge(lead.franchise_interest)}</div>
              )}
              {lead.years_experience && (
                <div className="flex justify-between"><span className="text-slate-500">Experience</span><span className="font-medium">{lead.years_experience} years</span></div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content - Activity Section */}
        <div className="lg:col-span-2">
          <Card>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start border-b rounded-none px-4">
                <TabsTrigger value="activity">Activity ({communications.length})</TabsTrigger>
                <TabsTrigger value="tasks">Tasks ({pendingTasks.length})</TabsTrigger>
                <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>

              {/* Activity Tab */}
              <TabsContent value="activity" className="p-4 space-y-4 m-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {['all', 'email', 'sms', 'call'].map(filter => (
                    <Button key={filter} variant={activityFilter === filter ? 'default' : 'outline'} size="sm" onClick={() => setActivityFilter(filter)} data-testid={`filter-${filter}`}>
                      {filter === 'all' ? 'All' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} (${communications.filter(c => c.type === filter).length})`}
                    </Button>
                  ))}
                </div>
                {filteredCommunications.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No activity yet. Start by sending an email or SMS.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCommunications.map(comm => (
                      <div key={comm.id} className="flex gap-3 p-3 border rounded-lg hover:border-indigo-200 transition-colors group">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${comm.type === 'email' ? 'bg-blue-100' : comm.type === 'sms' ? 'bg-green-100' : 'bg-amber-100'}`}>
                          {comm.type === 'email' && <Mail className="h-5 w-5 text-blue-600" />}
                          {comm.type === 'sms' && <MessageSquare className="h-5 w-5 text-green-600" />}
                          {comm.type === 'call' && <Phone className="h-5 w-5 text-amber-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">
                              {comm.type === 'email' ? comm.subject : comm.type === 'sms' ? `SMS to ${comm.to}` : `Call to ${comm.to || comm.phone_number}`}
                            </span>
                            {comm.call_outcome && <Badge variant="outline" className="text-xs">{comm.call_outcome}</Badge>}
                          </div>
                          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap line-clamp-3">{comm.content || comm.body || comm.message}</p>
                          <p className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                            <User className="h-3 w-3" />
                            {comm.user_name || 'System'} • {comm.created_at ? format(parseISO(comm.created_at), 'MMM d, yyyy h:mm a') : ''}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100" onClick={() => handleDeleteCommunication(comm.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Tasks Tab */}
              <TabsContent value="tasks" className="p-4 space-y-4 m-0">
                <Button size="sm" onClick={() => setTaskDialogOpen(true)} data-testid="add-task-btn"><Plus className="h-4 w-4 mr-1" /> Add Task</Button>
                {tasks.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No tasks yet. Create your first task.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingTasks.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-2">Pending ({pendingTasks.length})</h4>
                        {pendingTasks.map(task => (
                          <div key={task.id} className="flex items-center gap-3 p-3 border rounded-lg mb-2 hover:border-indigo-200">
                            <Checkbox checked={task.completed} onCheckedChange={() => handleToggleTask(task.id, task.completed)} />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{task.title}</p>
                              {task.description && <p className="text-xs text-slate-500">{task.description}</p>}
                              {task.due_date && <p className="text-xs text-slate-500">Due: {format(parseISO(task.due_date), 'MMM d, yyyy h:mm a')}</p>}
                            </div>
                            <Badge variant="outline">{task.task_type || 'Task'}</Badge>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteTask(task.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {completedTasks.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-2">Completed ({completedTasks.length})</h4>
                        {completedTasks.map(task => (
                          <div key={task.id} className="flex items-center gap-3 p-3 border rounded-lg mb-2 bg-slate-50 opacity-70">
                            <Checkbox checked={task.completed} onCheckedChange={() => handleToggleTask(task.id, task.completed)} />
                            <div className="flex-1">
                              <p className="text-sm font-medium line-through">{task.title}</p>
                            </div>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteTask(task.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Notes Tab */}
              <TabsContent value="notes" className="p-4 space-y-4 m-0">
                <div className="flex gap-2">
                  <Textarea 
                    value={newNote} 
                    onChange={e => setNewNote(e.target.value)} 
                    placeholder="Add a note..." 
                    rows={2}
                    className="flex-1"
                  />
                  <Button onClick={handleAddNote} disabled={saving || !newNote.trim()}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
                {notes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No notes yet. Add your first note above.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notes.map(note => (
                      <div key={note.id} className="p-3 border rounded-lg hover:border-indigo-200 group">
                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-slate-500">
                            {note.user_name || 'Unknown'} • {note.created_at ? formatDistanceToNow(parseISO(note.created_at), { addSuffix: true }) : ''}
                          </p>
                          <Button variant="ghost" size="sm" className="text-red-600 opacity-0 group-hover:opacity-100" onClick={() => handleDeleteNote(note.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* History/Audit Tab */}
              <TabsContent value="history" className="p-4 m-0">
                {auditLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No change history available.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditLogs.map(log => (
                      <div key={log.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm capitalize">{log.action}</span>
                          <span className="text-xs text-slate-500">
                            {log.created_at ? format(parseISO(log.created_at), 'MMM d, yyyy h:mm a') : ''}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">By {log.user_name || 'System'}</p>
                        {log.changes && Object.keys(log.changes).length > 0 && (
                          <div className="mt-2 text-xs space-y-1">
                            {Object.entries(log.changes).map(([field, change]) => (
                              <div key={field} className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">{field}:</span>
                                <span className="text-red-500 line-through">{String(change.old || 'empty')}</span>
                                <span>→</span>
                                <span className="text-green-600">{String(change.new || 'empty')}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>

      {/* Edit Lead Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Lead</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div><Label>First Name *</Label><Input value={formData.first_name || ''} onChange={e => setFormData({ ...formData, first_name: e.target.value })} /></div>
            <div><Label>Last Name</Label><Input value={formData.last_name || ''} onChange={e => setFormData({ ...formData, last_name: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} /></div>
            <div><Label>Current Employer</Label><Input value={formData.current_employer || ''} onChange={e => setFormData({ ...formData, current_employer: e.target.value })} /></div>
            <div><Label>Years Experience</Label><Input value={formData.years_experience || ''} onChange={e => setFormData({ ...formData, years_experience: e.target.value })} /></div>
            <div><Label>Territory Preference</Label><Input value={formData.territory_preference || ''} onChange={e => setFormData({ ...formData, territory_preference: e.target.value })} /></div>
            <div><Label>LinkedIn URL</Label><Input value={formData.linkedin_url || ''} onChange={e => setFormData({ ...formData, linkedin_url: e.target.value })} /></div>
            <div><Label>Address</Label><Input value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} /></div>
            <div><Label>City</Label><Input value={formData.city || ''} onChange={e => setFormData({ ...formData, city: e.target.value })} /></div>
            <div><Label>State</Label><Input value={formData.state || ''} onChange={e => setFormData({ ...formData, state: e.target.value })} /></div>
            <div><Label>Zip Code</Label><Input value={formData.zip_code || ''} onChange={e => setFormData({ ...formData, zip_code: e.target.value })} /></div>
            <div>
              <Label>Pipeline Status</Label>
              <Select value={formData.status || 'new'} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lead Status</Label>
              <Select value={formData.lead_status || 'new'} onValueChange={(val) => setFormData({ ...formData, lead_status: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Franchise Interest</Label>
              <Select value={formData.franchise_interest || 'medium'} onValueChange={(val) => setFormData({ ...formData, franchise_interest: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FRANCHISE_INTERESTS.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Lead Owner</Label><Input value={formData.lead_owner || ''} onChange={e => setFormData({ ...formData, lead_owner: e.target.value })} /></div>
            <div><Label>Lead Source</Label><Input value={formData.lead_source || ''} onChange={e => setFormData({ ...formData, lead_source: e.target.value })} /></div>
            {/* DNC/DNE Checkboxes */}
            <div className="col-span-2 flex items-center gap-6 py-2 border-t mt-2 pt-4">
              <div className="flex items-center gap-2">
                <Checkbox id="edit-dnc" checked={formData.dnc || false} onCheckedChange={(checked) => setFormData({...formData, dnc: checked})} />
                <Label htmlFor="edit-dnc" className="cursor-pointer">Do Not Call (DNC)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="edit-dne" checked={formData.dne || false} onCheckedChange={(checked) => setFormData({...formData, dne: checked})} />
                <Label htmlFor="edit-dne" className="cursor-pointer">Do Not Email (DNE)</Label>
              </div>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mb-2">
            <p className="text-sm text-slate-500">Task for</p>
            <p className="font-semibold text-slate-900">{lead.first_name} {lead.last_name}</p>
          </div>
          <div className="space-y-4 py-2">
            <div>
              <Label>Task Type</Label>
              <Select value={taskForm.task_type} onValueChange={(val) => setTaskForm({ ...taskForm, task_type: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Call">Call</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="Meeting">Meeting</SelectItem>
                  <SelectItem value="Follow-up">Follow-up</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Title *</Label><Input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Task title..." /></div>
            <div><Label>Description</Label><Textarea value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} rows={2} placeholder="Details..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Due Date</Label><Input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })} /></div>
              <div><Label>Due Time</Label><Input type="time" value={taskForm.due_time} onChange={e => setTaskForm({ ...taskForm, due_time: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <EmailMergeDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        lead={lead}
        templates={templates}
        onSuccess={() => fetchData()}
      />

      {/* SMS Dialog */}
      <DraggableDialog open={smsDialogOpen} onClose={() => setSmsDialogOpen(false)} title="Send SMS" maxWidth="max-w-md">
        <div className="space-y-4">
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-500">Sending SMS to</p>
            <p className="font-semibold text-slate-900">{lead.first_name} {lead.last_name}</p>
          </div>
          {templates.filter(t => t.type === 'sms').length > 0 && (
            <div><Label>SMS Template</Label><TemplateCombobox templates={templates.filter(t => t.type === 'sms')} value={smsForm.template_id} onChange={handleSmsTemplateChange} placeholder="Select a template..." /></div>
          )}
          <div><Label>To</Label><Input value={smsForm.to} onChange={e => setSmsForm({ ...smsForm, to: e.target.value })} placeholder="Phone number" /></div>
          <div>
            <div className="flex items-center justify-between"><Label>Message</Label><VariablesHelper onInsert={v => insertVariable(v, 'sms', 'message')} compact /></div>
            <Textarea value={smsForm.message} onChange={e => setSmsForm({ ...smsForm, message: e.target.value })} rows={4} placeholder="Type your message..." />
            <p className="text-xs text-slate-500 mt-1">{smsForm.message.length} / 160 characters</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendSms} disabled={saving || !smsForm.message.trim()}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send SMS</Button>
          </div>
        </div>
      </DraggableDialog>

      {/* Call Log Dialog */}
      <LogCallDialog
        open={callDialogOpen}
        onClose={() => setCallDialogOpen(false)}
        lead={lead}
        callForm={callForm}
        setCallForm={setCallForm}
        onSubmit={handleLogCall}
        saving={saving}
        templates={templates}
        leadId={id}
        onClickToCall={handleClickToCall}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete {lead.first_name} {lead.last_name}? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Active Call Popup (for non-WebRTC calls) */}
      <ActiveCallPopup
        open={activeCallPopupOpen}
        phoneNumber={activeCallNumber}
        leadName={lead ? `${lead.first_name} ${lead.last_name}`.trim() : 'Unknown'}
        leadCompany={lead?.current_employer}
        callStatus={activeCallStatus}
        onDisconnect={handleDisconnectCall}
        onLogCall={handleLogCallFromPopup}
        onClose={() => {
          setActiveCallPopupOpen(false);
          setActiveCallNumber(null);
          setActiveCallStatus('calling');
        }}
      />

      {/* WebRTC Call Dialog (for browser-based calling with audio) */}
      <TelnyxWebRTCCall
        isOpen={webrtcCallOpen}
        onClose={() => { setWebrtcCallOpen(false); setWebrtcCallNumber(null); }}
        phoneNumber={webrtcCallNumber}
        leadName={lead ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : ''}
        companyName={lead?.current_employer || ''}
        onCallConnected={handleWebRTCCallConnected}
        onCallEnded={handleWebRTCCallEnded}
        onDurationUpdate={handleDurationUpdate}
        onError={handleWebRTCError}
      />

      {/* Add to List Dialog */}
      <Dialog open={addToListDialogOpen} onOpenChange={setAddToListDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-indigo-600" />
              Add to List
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {availableLists.length === 0 ? (
              <p className="text-sm text-slate-500 text-center">This lead is already in all available lists.</p>
            ) : (
              <Select value={selectedListToAdd} onValueChange={setSelectedListToAdd}>
                <SelectTrigger data-testid="select-list-dropdown">
                  <SelectValue placeholder="Select a list..." />
                </SelectTrigger>
                <SelectContent>
                  {availableLists.map(list => (
                    <SelectItem key={list.id} value={list.id}>{list.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddToListDialogOpen(false); setSelectedListToAdd(''); }}>Cancel</Button>
            <Button onClick={handleAddToList} disabled={!selectedListToAdd || availableLists.length === 0} data-testid="confirm-add-to-list">Add to List</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
